import type { RegisteredMarketInfo } from '@/types/api';
import { usePost, CPLAT_API_ROUTES } from '@/api';
import { useAppSelector } from '@/redux/store';
import { useToast } from '@/hooks/V3/useToast';
import EmptyFaceIcon from '@/assets/icons/EmptyFaceIcon.svg?react';
import { NEED_INVITE_MANNAGER } from './_constants/const';
import { useState } from 'react';
import { Button } from '@/components/atoms/NewPcButton';
import { ChevronRight } from 'lucide-react';
import ChangeSequence from './ChangeSequence';
import { ROUTES } from '@/routes';
import { useNavigate } from 'react-router-dom';
import MarketComment from './MarketComment';
import InfoFillGrayIcon from '@/assets/icons/InfoFillGrayIcon.svg?react';

interface RegisteredMarketListProps {
  reFetchList: () => void;
  registeredMarketList: RegisteredMarketInfo[]; // 실제 타입으로 변경 필요
  isEmptyRegistedMarket: boolean;
}
const RegisteredMarketList = ({
  reFetchList,
  registeredMarketList,
  isEmptyRegistedMarket,
  reFetchReference,
  referenceMarketArray,
}: RegisteredMarketListProps) => {
  const navigate = useNavigate();

  const user = useAppSelector((state) => state.user);
  const { showToast } = useToast();
  const [sequenceDialogOpen, setSequenceDialogOpen] = useState(false);
  const [tempSequence, setTempSequence] = useState<RegisteredMarketInfo[]>([]);

  const { mutate: updateSequence, isPending: isUpdateSequence } = usePost(
    CPLAT_API_ROUTES.setMarketStoreSequence,
  );

  const handleUpdateSequence = () => {
    updateSequence(
      {
        cplatToken: user.CplatToken,
        MarketStoreList: tempSequence.map((market, index) => ({
          ...market,
          marketAlias: market.StoreName, // 또는 marketAlias: market.StoreName (API 명세에 맞게)
          MarketSeqNo: index, // 또는 marketSeqNo: index (API 명세에 맞게)
        })),
      },
      {
        onSuccess: (res) => {
          const { code } = res.data;

          if (code === '200') {
            showToast('success', '스토어 순서를 변경했어요');
            reFetchList();
            reFetchReference();
          } else {
            showToast('warning', '스토어 순서 변경을 실패하였어요');
          }
        },
        onError: () => {
          showToast('warning', '스토어 순서 변경을 실패하였어요');
        },
        onSettled: () => {
          setSequenceDialogOpen(false);
        },
      },
    );
  };

  const safeBase64Encode = (str?: string) => {
    if (!str) return 'null';
    try {
      return encodeURIComponent(btoa(unescape(encodeURIComponent(str))));
    } catch (e) {
      return 'null';
    }
  };

  const handleClickMarket = (market: RegisteredMarketInfo) => {
    // 마켓 클릭 시 동작 정의 (예: 상세 페이지로 이동)

    const idValue =
      market.MarketNameEnglish === 'coupang'
        ? safeBase64Encode(market.VendorId)
        : safeBase64Encode(market.MarketId);

    navigate(`${ROUTES.changePassword}/${market.MarketNameEnglish}?id=${idValue}`);
  };

  return (
    <>
      <div className="flex flex-col gap-[6px]">
        <div className="flex justify-between">
          <div className="text-gr-900 Body1S16">연동 중인 마켓</div>
          <Button
            disabled={registeredMarketList?.length <= 1 || isUpdateSequence}
            variant="line"
            className="h-[34px] border-gr-200 px-[10px] py-[6px] text-gr-700 shadow-none Body5M14"
            onClick={() => setSequenceDialogOpen(true)}
          >
            스토어 순서편집
          </Button>
        </div>
        <div className="flex flex-col rounded-[16px] bg-white p-5">
          {referenceMarketArray.length > 0 ? (
            referenceMarketArray.map((refMarket) => {
              // registeredMarketList에서 실제 마켓 데이터 찾기 (MarketId, StoreId 등으로 매칭)
              const market = registeredMarketList.find(
                (m) =>
                  m.MarketNameEnglish === refMarket.MarketNameEnglish &&
                  m.MarketAlias === refMarket.StoreName,
              );

              const hasComment = !!market?.StoreComment && market.StoreComment.trim() !== '';
              const showOverlay = hasComment && market?.StoreComment !== NEED_INVITE_MANNAGER;

              // const isPending =
              //   market?.IsTossConfirmed === false && market?.MarketNameEnglish === 'toss';
              return (
                <div
                  key={`${refMarket.MarketNameEnglish}_${refMarket.MarketId}_${
                    refMarket.StoreId || ''
                  }`}
                  onClick={() => handleClickMarket(market || refMarket)}
                  className="relative flex cursor-pointer items-center justify-between border-b py-4 first:pt-0 last:border-0 last:pb-0"
                >
                  {/* 오버레이 조건 변경 */}
                  {showOverlay && (
                    <div className="pointer-events-none absolute inset-0 z-[10] rounded-[8px] bg-gr-50/50 backdrop:blur-sm" />
                  )}
                  <div className="z-0 flex items-center gap-3">
                    <img
                      src={market?.MarketIconUrl || refMarket.MarketIconUrl}
                      alt={`${market?.MarketAlias || refMarket.MarketAlias || ''} logo`}
                      className="h-7 w-7 rounded-full"
                    />
                    <div className="flex flex-col gap-[2px]">
                      <div className="text-gr-800 Body4S14">
                        {market?.MarketAlias || market?.MarketId}
                      </div>
                      {/* 빨간색 코멘트 표시 */}
                      {hasComment && <MarketComment storeComment={market?.StoreComment || ''} />}
                      {/* {isPending && <TossPending />} */}
                    </div>
                  </div>
                  <ChevronRight className="z-20 h-6 w-6 text-gr-600" />
                </div>
              );
            })
          ) : (
            <div className="flex min-h-[300px] flex-col items-center justify-center gap-[26px] text-gr-500 Body4S14">
              <EmptyFaceIcon />
              등록된 마켓이 없습니다
            </div>
          )}
        </div>
      </div>
      <ChangeSequence
        tempSequence={tempSequence}
        setTempSequence={setTempSequence}
        registeredMarketList={registeredMarketList}
        referenceMarketArray={referenceMarketArray}
        handleUpdateSequence={handleUpdateSequence}
        open={sequenceDialogOpen}
        onOpenChange={setSequenceDialogOpen}
      />
    </>
  );
};

export default RegisteredMarketList;

export const TossPending = () => {
  return (
    <div className="flex w-fit gap-1 rounded-[8px] bg-gr-100 px-[6px] py-[4px]  text-gr-700 Caption1M12 ">
      <InfoFillGrayIcon />
      마켓 연동 대기중이에요
    </div>
  );
};
